import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  EyeIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface Submission {
  id: string;
  formId: string;
  formName: string;
  submittedBy: string;
  submittedAt: Date;
  status: 'pending' | 'submitted' | 'processing' | 'completed' | 'hibernated';
  data: any;
  notes?: string;
}

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
}

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  hibernated: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
};

const STATUS_ICONS = {
  pending: ClockIcon,
  submitted: CheckCircleIcon,
  processing: DocumentTextIcon,
  completed: CheckCircleIcon,
  hibernated: ArchiveBoxIcon
};

const MySubmissions: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [formFields, setFormFields] = useState<Record<string, FormField[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (user?.id) {
      loadSubmissions();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadSubmissions = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // console.log('🔍 Loading submissions for user:', user.id);
      
      const q = query(
        collection(db, 'formSubmissions'),
        where('submittedBy', '==', user.id),
        orderBy('createdAt', 'desc') // Changed from submittedAt to createdAt
      );
      const snapshot = await getDocs(q);
      
      // console.log('📦 Found submissions:', snapshot.docs.length);
      
      const fetchedSubmissions = snapshot.docs.map(doc => {
        const data = doc.data();
        // console.log('📄 Submission data:', { 
        //   id: doc.id, 
        //   templateId: data.templateId, 
        //   templateName: data.templateName,
        //   hasTemplateId: !!data.templateId 
        // });
        return {
          id: doc.id,
          formId: data.templateId || '',
          formName: data.templateName || 'Unknown Form',
          submittedBy: data.submittedBy,
          submittedAt: data.createdAt?.toDate() || new Date(), // Use createdAt
          status: data.status || 'pending',
          data: data.data || {},
          notes: data.notes
        } as Submission;
      });
      
      // console.log('✅ Formatted submissions:', fetchedSubmissions);
      setSubmissions(fetchedSubmissions);
      
      // Load form fields for each unique form (filter out empty IDs)
      const uniqueFormIds = Array.from(new Set(fetchedSubmissions.map(s => s.formId).filter(id => id && id.trim() !== '')));
      // console.log('📋 Unique form IDs:', uniqueFormIds);
      if (uniqueFormIds.length > 0) {
        await loadFormFields(uniqueFormIds);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error(t('common.errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadFormFields = async (formIds: string[]) => {
    const fieldsMap: Record<string, FormField[]> = {};
    
    for (const formId of formIds) {
      // Skip empty or invalid IDs
      if (!formId || formId.trim() === '') {
        console.warn('Skipping empty form ID');
        continue;
      }
      
      try {
        const formDoc = await getDoc(doc(db, 'formTemplates', formId));
        if (formDoc.exists()) {
          const formData = formDoc.data();
          fieldsMap[formId] = formData.fields || [];
        }
      } catch (error) {
        console.error(`Error loading form ${formId}:`, error);
      }
    }
    
    setFormFields(fieldsMap);
  };

  const getFieldLabel = (formId: string, fieldId: string): string => {
    const fields = formFields[formId];
    // console.log('🔍 getFieldLabel called:', { formId, fieldId, hasFields: !!fields, fieldsCount: fields?.length });
    if (!fields) {
      // console.log('❌ No fields found for form:', formId, 'Available forms:', Object.keys(formFields));
      return fieldId;
    }
    
    const field = fields.find(f => f.id === fieldId);
    if (!field) {
      // console.log('❌ Field not found:', fieldId, 'Available fields:', fields.map(f => ({ id: f.id, label: f.label })));
      return fieldId;
    }
    // console.log('✅ Found field:', { id: field.id, label: field.label });
    return field?.label || field?.name || fieldId;
  };

  const getStatusIcon = (status: string) => {
    const Icon = STATUS_ICONS[status as keyof typeof STATUS_ICONS] || ClockIcon;
    return <Icon className="h-5 w-5" />;
  };

  // Pagination
  const totalPages = Math.ceil(submissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubmissions = submissions.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('customerPortal.submissions.noSubmissions')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('customerPortal.submissions.description')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {currentSubmissions.map((submission) => (
        <div
          key={submission.id}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
        >
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${STATUS_COLORS[submission.status]}`}>
                  {getStatusIcon(submission.status)}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {submission.formName}
                </h3>
                <div className="flex items-center space-x-3 mt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {submission.submittedAt.toLocaleDateString('hu-HU')} {submission.submittedAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[submission.status]}`}>
                    {t(`customerPortal.submissions.status.${submission.status}`)}
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => {
                // console.log('🔍 Opening submission details:', { 
                //   id: submission.id, 
                //   formId: submission.formId, 
                //   formName: submission.formName 
                // });
                setSelectedSubmission(submission);
              }}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('customerPortal.submissions.viewDetails')}
            >
              <EyeIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {startIndex + 1}-{Math.min(endIndex, submissions.length)} / {submissions.length} beküldés
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Előző
            </button>
            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Következő
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {selectedSubmission && (() => {
        // console.log('📋 Modal opened with submission:', { 
        //   id: selectedSubmission.id, 
        //   formId: selectedSubmission.formId, 
        //   formName: selectedSubmission.formName,
        //   dataKeys: Object.keys(selectedSubmission.data || {})
        // });
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('customerPortal.submissions.details')}
                </h2>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customerPortal.submissions.formName')}
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100">
                    {selectedSubmission.formName}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('common.status')}
                  </label>
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedSubmission.status]}`}>
                    {getStatusIcon(selectedSubmission.status)}
                    <span>{t(`customerPortal.submissions.status.${selectedSubmission.status}`)}</span>
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customerPortal.submissions.submissionDate')}
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100">
                    {selectedSubmission.submittedAt.toLocaleDateString('hu-HU')} {selectedSubmission.submittedAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                {selectedSubmission.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('customerPortal.submissions.notes')}
                    </label>
                    <p className="text-base text-gray-900 dark:text-gray-100">
                      {selectedSubmission.notes}
                    </p>
                  </div>
                )}

                {/* Submitted Data */}
                {selectedSubmission.data && Object.keys(selectedSubmission.data).length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('customerPortal.submissions.submittedData')}
                    </label>
                    <div className="space-y-3">
                      {Object.entries(selectedSubmission.data).map(([fieldId, value]) => {
                        const isFileUrl = typeof value === 'string' && value.includes('firebasestorage.googleapis.com');
                        const isFileArray = Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' && value[0].includes('firebasestorage.googleapis.com');
                        
                        return (
                        <div key={fieldId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            {getFieldLabel(selectedSubmission.formId, fieldId)}
                          </p>
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {typeof value === 'boolean' ? (
                              <span className={value ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                {value ? '✓ Igen' : '✗ Nem'}
                              </span>
                            ) : isFileUrl ? (
                              <a 
                                href={value} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Fájl letöltése
                              </a>
                            ) : isFileArray ? (
                              <div className="space-y-1">
                                {value.map((url: string, idx: number) => (
                                  <a 
                                    key={idx}
                                    href={url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Fájl {idx + 1} letöltése
                                  </a>
                                ))}
                              </div>
                            ) : Array.isArray(value) ? (
                              <ul className="list-disc list-inside space-y-1">
                                {value.map((item, idx) => (
                                  <li key={idx}>{String(item)}</li>
                                ))}
                              </ul>
                            ) : typeof value === 'object' && value !== null ? (
                              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              String(value)
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('customerPortal.submissions.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default MySubmissions;
